import { PaymentElement } from "@stripe/react-stripe-js";
import { Form, Button } from "react-bootstrap";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { onboardingActions } from "./../store/onboarding.slice";
import { paymentActions } from "./../store/payment.slice";
import HttpStatus from "./../helpers/status.enum";
import SpinnerLoader from "../components/SpinnerLoader";
import { toast } from "react-toastify";

// Safely serialize any value — handles non-enumerable Stripe error objects
function safeSerialize(val) {
  if (val === null || val === undefined) return val;
  try {
    return JSON.parse(JSON.stringify(val));
  } catch {
    return String(val);
  }
}

const CheckoutForm = ({
  closeCheckoutModal,
  // gotoHomePage,
  nextUpsellCardStep,
  bookingSlug,
  clientSecret,
  customerId,
  stripeId,
  paymentSlug,
  intentType,
  cmsContent,
  securityDepositAlert,
  isHoldPurchased = false, // This is used to check if the switch to waiver button is clicked
  logEvent = () => {},
}) => {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();

  const [loader, setLoader] = useState(false);

  useEffect(() => { }, []);

  const [errorCardMessage, setErrorCardMessage] = useState(null);

  // Log component mount with full context — stripe/elements null here = stripePromise mismatch
  useEffect(() => {
    logEvent("checkout_form_mounted", "initiated", {
      intent_type: intentType,
      stripe_available: !!stripe,
      elements_available: !!elements,
      client_secret: clientSecret ?? null,
      client_secret_present: !!clientSecret,
      booking_slug: bookingSlug ?? null,
      customer_id: customerId ?? null,
      stripe_id: stripeId ?? null,
      payment_slug: paymentSlug ?? null,
    });
  }, []);

  // Log when stripe/elements become available (async resolution after mount)
  useEffect(() => {
    if (stripe && elements) {
      logEvent("payment_element_mounted", "mounted", {
        intent_type: intentType,
        client_secret: clientSecret ?? null,
      });
    }
  }, [stripe, elements]);

  // Log component unmount
  useEffect(() => {
    return () => {
      logEvent("component_unmounted", "unmounted", { intent_type: intentType });
    };
  }, []);

  const handlepageLoaderModalOpen = async (event) => {
    setLoader(true);
    event.preventDefault();

    logEvent("submit_button_clicked", "initiated", {
      intent_type: intentType,
      stripe_available: !!stripe,
      elements_available: !!elements,
      loader_state: loader,
    });

    if (!stripe || !elements) {
      logEvent("submit_blocked_stripe_not_ready", "failed", {
        intent_type: intentType,
        stripe_available: !!stripe,
        elements_available: !!elements,
        client_secret: clientSecret ?? null,
        error: {
          message: "stripe or elements not initialized at submit time",
          type: "stripe_not_ready",
        },
      });
      return;
    }

    logEvent("payment_submission_started", "initiated", { intent_type: intentType });

    let payementResult;
    try {
      payementResult = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });
    } catch (confirmErr) {
      logEvent("payment_confirm_exception", "failed", {
        intent_type: intentType,
        error: {
          message: confirmErr?.message ?? "stripe.confirmPayment threw",
          type: "confirm_exception",
          raw: safeSerialize(confirmErr),
        },
      });
      setLoader(false);
      return;
    }

    if (payementResult.error) {
      logEvent("payment_confirmation_failed", "failed", {
        intent_type: intentType,
        error: {
          message: payementResult.error.message,
          code: payementResult.error.code ?? null,
          type: payementResult.error.type ?? null,
          decline_code: payementResult.error.decline_code ?? null,
          param: payementResult.error.param ?? null,
          doc_url: payementResult.error.doc_url ?? null,
        },
        raw_error: safeSerialize(payementResult.error),
        full_result: safeSerialize(payementResult),
      });
      setLoader(false);
      setErrorCardMessage(payementResult?.error?.message);
      setTimeout(() => {
        setErrorCardMessage(null);
      }, 2000);
    } else {
      logEvent("payment_confirmation_succeeded", "success", {
        intent_type: intentType,
        payment_intent_status: payementResult?.paymentIntent?.status ?? null,
        payment_intent_id: payementResult?.paymentIntent?.id ?? null,
        full_result: safeSerialize(payementResult),
      });

      const formData = {
        booking_slug: bookingSlug,
        is_house_rules_accepted: 0,
        is_signed_on_dotted_line: 0,
        is_verification_ids_uploaded: 0,

        is_deposit_option: 1,
        deposit_option: intentType,
        card_token: stripeId,
        is_arrival_info_accepted: 0,
        form_submission_at: 0,
        is_signed_via_otp: 0,
      };

      setErrorCardMessage(null);

      logEvent("onboarding_submit_started", "initiated", {
        intent_type: intentType,
        booking_slug: bookingSlug,
      });

      try {
        const result = await dispatch(
          onboardingActions.submitOnboardingSteps(formData)
        );

        logEvent("onboarding_submit_completed", result?.payload?.status === HttpStatus.HTTP_OK ? "success" : "failed", {
          intent_type: intentType,
          status: result?.payload?.status ?? null,
          message: result?.payload?.message ?? null,
          payload: safeSerialize(result?.payload),
        });

        if (result?.payload?.status === HttpStatus.HTTP_OK) {
          const paymentFormData = {
            payment_slug: paymentSlug,
            booking_slug: bookingSlug,
            status_id: 1,
            final_payment_response: payementResult,
          };

          logEvent("update_payment_status_started", "initiated", {
            intent_type: intentType,
            payment_slug: paymentSlug,
            booking_slug: bookingSlug,
          });

          try {
            const resultPayment = await dispatch(
              paymentActions.updatePaymentStatus(paymentFormData)
            );

            logEvent("update_payment_status_completed", resultPayment?.payload?.status === HttpStatus.HTTP_OK ? "success" : "failed", {
              intent_type: intentType,
              status: resultPayment?.payload?.status ?? null,
              message: resultPayment?.payload?.message ?? null,
              payload: safeSerialize(resultPayment?.payload),
            });

            if (resultPayment?.payload?.status === HttpStatus.HTTP_OK) {
              // gotoHomePage(true);
              if(intentType === 1){
                if(!isHoldPurchased) {
                  toast(resultPayment?.payload?.message);
                }
              }
              nextUpsellCardStep();
            }
          } catch (error) {
            console.error(
              "Error occurred while updating payment status :",
              error
            );
            logEvent("update_payment_status_exception", "failed", {
              intent_type: intentType,
              error: {
                message: error?.message ?? "updatePaymentStatus threw",
                type: "exception",
                raw: safeSerialize(error),
              },
            });
          }
        }
      } catch (error) {
        console.error(
          "Error occurred while updating onbording status :",
          error
        );
        logEvent("onboarding_submit_exception", "failed", {
          intent_type: intentType,
          error: {
            message: error?.message ?? "submitOnboardingSteps threw",
            type: "exception",
            raw: safeSerialize(error),
          },
        });
      }
      setLoader(false);
    }
  };

  const setShowCreditCard = async () => {
    logEvent("cancel_button_clicked", "initiated", {
      intent_type: intentType,
      loader_state: loader,
    });
    closeCheckoutModal(false);
  };

  return (
    <Form>
      <h2 className="mb-2">
        {intentType === 1 ? (
          <span
            dangerouslySetInnerHTML={{
              __html: cmsContent?.checkout_card_title_damage_waiver,
            }}
          />
        ) : (
          <span
            dangerouslySetInnerHTML={{
              __html: cmsContent?.checkout_card_title_security_hold,
            }}
          />
        )}
      </h2>
      <h6 dangerouslySetInnerHTML={{
        __html: intentType === 1 ? securityDepositAlert?.non_refundable_description : securityDepositAlert?.refundable_description,
      }}>
      </h6>
      <h6 dangerouslySetInnerHTML={{
        __html: intentType === 1 ? securityDepositAlert?.description : "",
      }}>
      </h6>
      <hr />
      <div className="paymentmethod pt-0 pb-2">
        <PaymentElement
          onReady={() =>
            logEvent("payment_element_ready", "ready", {
              intent_type: intentType,
              stripe_available: !!stripe,
              elements_available: !!elements,
              client_secret: clientSecret ?? null,
            })
          }
          onLoadError={(err) => {
            const stripeErr = err?.error ?? err;
            logEvent("payment_element_load_error", "failed", {
              intent_type: intentType,
              client_secret: clientSecret ?? null,
              client_secret_present: !!clientSecret,
              stripe_available: !!stripe,
              elements_available: !!elements,
              error: {
                message: stripeErr?.message ?? "PaymentElement failed to load",
                code: stripeErr?.code ?? null,
                type: stripeErr?.type ?? null,
                decline_code: stripeErr?.decline_code ?? null,
                param: stripeErr?.param ?? null,
                doc_url: stripeErr?.doc_url ?? null,
              },
              raw_error: safeSerialize(err),
            });
            window.location.reload()
          }}
          onChange={(e) =>
            logEvent("payment_element_change", "change", {
              intent_type: intentType,
              complete: e?.complete ?? null,
              empty: e?.empty ?? null,
              value_type: e?.value?.type ?? null,
            })
          }
          onFocus={() =>
            logEvent("payment_element_focus", "focus", { intent_type: intentType })
          }
          onBlur={() =>
            logEvent("payment_element_blur", "blur", { intent_type: intentType })
          }
        />
      </div>
      {errorCardMessage ? (
        <small className="text-danger mb-3 d-block">{errorCardMessage}</small>
      ) : (
        ""
      )}
      <div className="d-flex justify-content-between">
        <Button
          className="outlinebutton"
          onClick={() => setShowCreditCard(false)}
          disabled={loader ? true : false}
        >
          Cancel
        </Button>

        {/* disabled={buttonClicked} */}
        <Button
          className="fillbutton"
          onClick={handlepageLoaderModalOpen}
          disabled={loader ? true : false}
        >
          {loader ? (
            <>
              <SpinnerLoader />
            </>
          ) : (
            "Submit"
          )}
        </Button>
      </div>
    </Form>
  );
};

export default CheckoutForm;
